/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '../../../theme/theme';

export interface LabelValueRowProps {
  label: string;
  value: string;
  unit?: string;
  emphasized?: boolean;
}

interface LabelValueRowStyleProps {
  emphasized?: boolean;
}

const useStyles = makeStyles(() => ({
  row : {
    display        : 'flex',
    width          : '100%',
    alignItems     : 'baseline',
    justifyContent : 'space-between',
    gap            : '8px'
  },
  label : {
    fontFamily    : 'Roboto',
    fontWeight    : 700,
    fontSize      : ({emphasized}: LabelValueRowStyleProps) => emphasized ? '14px' : '10px',
    lineHeight    : '14px',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    color         : dlsColor('grey3')
  },
  valueGroup : {
    display    : 'flex',
    alignItems : 'baseline',
    gap        : '4px'
  },
  value : {
    fontFamily : 'Inter',
    fontWeight : ({emphasized}: LabelValueRowStyleProps) => emphasized ? 600 : 500,
    fontSize   : '18px',
    lineHeight : '22px',
    color      : dlsColor('grey2')
  },
  unit : {
    fontFamily : 'Inter',
    fontWeight : 500,
    fontSize   : '13px',
    lineHeight : '16px',
    color      : dlsColor('grey2')
  }
}));

/**
 * LabelValueRow renders the current theme's shared Spreader label/value row
 * - an uppercase Roboto label on the left and a right-aligned Inter
 * value (with an optional smaller unit suffix, e.g. "mi"/"gal") on the
 * right. `CardItemRow` (`@ion/dls/src/layout/CardItemRow`) was checked
 * first and doesn't fit here - its typography is the older `chipButton`
 * DLS token and it has no unit-suffix concept, whereas the Spreader Type 1
 * Figma spec calls for exact Roboto/Inter sizes (10-14px label,
 * 13-18px value) that don't match. `emphasized` renders both the label and
 * value at a heavier weight/size, for the "Total" row that closes out the
 * Granular Info and Liquid Totals blocks.
 *
 * Per the Figma mockup, a row whose value is legitimately "N/A" or "0"
 * still renders that value - callers only omit the row entirely (by not
 * rendering a `LabelValueRow` at all) when the underlying field is truly
 * absent/undefined.
 *
 * ```typescript
 *
 *   import {LabelValueRow} from
 *     '@ion/dls/src/components/assetdetails/current/LabelValueRow';
 *   ...
 *
 *   <LabelValueRow label='Granular Material Name'
 *     value='Salty' />
 *   <LabelValueRow label='Granular Total'
 *     value='2,033,344'
 *     unit='mi'
 *     emphasized />
 *
 * ```
 *
 */
export const LabelValueRow = ({
  label,
  value,
  unit,
  emphasized = false
}: LabelValueRowProps) => {
  const classes = useStyles({emphasized});
  return (
    <div className={classes.row}>
      <span className={classes.label}>
        {label}
      </span>
      <span className={classes.valueGroup}>
        <span className={classes.value}>
          {value}
        </span>
        {unit && (
          <span className={classes.unit}>
            {unit}
          </span>
        )}
      </span>
    </div>
  );
};

export default memo(LabelValueRow);
